import {
    faArrowsRotate,
    faCirclePlay,
    faEllipsisVertical,
    faTrash,
    faUpRightAndDownLeftFromCenter,
} from '@fortawesome/pro-regular-svg-icons';
import {faPlay} from '@fortawesome/pro-solid-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Button, Dropdown, Flex, Image, Popover, Typography} from 'antd';
import React, {useCallback, useState} from 'react';

import {gray8} from '../../../../../../../../css/var-export.scss';
import {useLocale} from '../../../../../../../../provider/locale/locale-hook';
import {Locale} from '../../../../../../../../provider/locale/localization';
import {isCompanyGalleryVideo} from '../../../../../../../../service/company-v2/types/company-gallery-type';
import {
    CompanyImageFormType,
    CompanyVideoFormType,
} from '../../../../../../../../service/company-v2/types/company-logo-type';
import {classNames} from '../../../../../../../../util/css';

import {GalleryVideoDuration} from './gallery-video-duration';
import * as styles from './gallery-image.scss';

type PropsType = {
    inputImage: CompanyImageFormType | CompanyVideoFormType;
    className?: string;
    errorMessage?: Array<string>;
    modifications?: Array<string>;
    onRemove: () => void;
};

const VIDEO_FILE_EXTENSIONS = /\.(mp4|webm|ogg|mov|avi|mkv|m4v|3gp)(\?.*)?$/i;

function isDirectVideoFile(url: string): boolean {
    return url.startsWith('blob:') || VIDEO_FILE_EXTENSIONS.test(url);
}

function openVideoUrl(url: string): void {
    if (isDirectVideoFile(url)) {
        const html = [
            '<!DOCTYPE html><html>',
            '<head><style>',
            '*{margin:0;padding:0;box-sizing:border-box}',
            'body{background:#000;display:flex;align-items:center;justify-content:center;width:100vw;height:100vh}',
            'video{max-width:100vw;max-height:100vh}',
            '</style></head>',
            `<body><video autoplay muted controls src="${url}"></video></body>`,
            '</html>',
        ].join('');
        const blob = new window.Blob([html], {type: 'text/html'});
        // eslint-disable-next-line no-restricted-globals
        const blobUrl = URL.createObjectURL(blob);

        window.open(blobUrl, '_blank');
    } else {
        window.open(url, '_blank');
    }
}

function renderZoomOnlyToolbar(
    _node: React.ReactElement,
    info: {icons: {zoomOutIcon: React.ReactNode; zoomInIcon: React.ReactNode}}
): React.ReactNode {
    return (
        <div className="ant-image-preview-operations">
            {info.icons.zoomOutIcon}
            {info.icons.zoomInIcon}
        </div>
    );
}

// eslint-disable-next-line complexity
export function GalleryMediaItem(props: PropsType): JSX.Element {
    const {className, inputImage, errorMessage, modifications, onRemove} = props;
    const {objectUrl} = inputImage;

    const fullClassName = classNames(styles.input_image_list_item, className);
    const {getLocalizedString} = useLocale();
    const [previewOpen, setPreviewOpen] = useState(false);
    const [popoverOpen, setPopoverOpen] = useState(false);
    const [modificationsPopoverOpen, setModificationsPopoverOpen] = useState(false);

    const handleDelete = useCallback(() => {
        if (objectUrl) {
            // eslint-disable-next-line no-restricted-globals
            URL.revokeObjectURL(objectUrl);
        }

        onRemove();
    }, [objectUrl, onRemove]);

    const mediaIsVideo = isCompanyGalleryVideo(inputImage);

    const url = 'url' in inputImage ? inputImage.url : null;
    const thumbnail = mediaIsVideo ? inputImage.thumbnail : null;
    const video = mediaIsVideo ? inputImage.video : null;
    const videoUrl = video || objectUrl;

    function mediaHandleClick(event: React.MouseEvent<HTMLDivElement>) {
        event.stopPropagation();

        if (mediaIsVideo) {
            if (videoUrl) {
                openVideoUrl(videoUrl);
            }

            return;
        }

        setPreviewOpen(true);
    }

    const media = (
        <>
            <Image
                className={styles.input_image_list_item__image}
                onClick={mediaHandleClick}
                preview={
                    mediaIsVideo
                        ? false
                        : {
                              onVisibleChange: (value) => setPreviewOpen(value),
                              toolbarRender: renderZoomOnlyToolbar,
                              visible: previewOpen,
                          }
                }
                src={(thumbnail || url || objectUrl) ?? undefined} // eslint-disable-line no-undefined
            />
            <GalleryVideoDuration media={inputImage} />
            {mediaIsVideo && (
                <Button className={styles.InputImage_playVideoButton} onClick={mediaHandleClick} type="text">
                    <FontAwesomeIcon className={styles.InputImage_icon} icon={faPlay} />
                </Button>
            )}
            <Flex className={styles.input_image_list_item__overlay} onClick={mediaHandleClick}>
                <Dropdown
                    className={styles.InputImage_dropdown}
                    menu={{
                        items: [
                            {
                                icon: <FontAwesomeIcon color={gray8} height={16} icon={faTrash} width={16} />,
                                label: getLocalizedString('BUTTON__DELETE'),
                                key: 'delete',
                                onClick: ({domEvent}) => {
                                    domEvent.stopPropagation();
                                    handleDelete();
                                },
                            },
                            {
                                icon: (
                                    <FontAwesomeIcon
                                        color={gray8}
                                        height={16}
                                        icon={mediaIsVideo ? faCirclePlay : faUpRightAndDownLeftFromCenter}
                                        width={16}
                                    />
                                ),
                                label: getLocalizedString(
                                    mediaIsVideo ? 'COMPANY_FORM__SHOW_VIDEO' : 'COMPANY_FORM__SHOW'
                                ),
                                key: 'edit',
                                onClick: ({domEvent}) => {
                                    domEvent.stopPropagation();

                                    if (mediaIsVideo) {
                                        if (videoUrl) {
                                            openVideoUrl(videoUrl);
                                        }
                                    } else {
                                        setPreviewOpen(true);
                                    }

                                    setPopoverOpen(false);
                                },
                            },
                        ],
                    }}
                    placement="bottomLeft"
                    trigger={['click']}
                >
                    <Button onClick={(event) => event.stopPropagation()} type="text">
                        <FontAwesomeIcon icon={faEllipsisVertical} />
                    </Button>
                </Dropdown>
            </Flex>
        </>
    );

    if (errorMessage) {
        return (
            <div className={fullClassName}>
                <Popover
                    content={
                        <>
                            <Typography.Paragraph className={styles.error_text}>
                                <Locale stringKey="COMPANY_FORM__GALLERY__ERROR__DESCRIPTION" />
                            </Typography.Paragraph>

                            {errorMessage.map((message) => (
                                <Typography.Paragraph className={styles.error_text} key={message}>
                                    {message}
                                </Typography.Paragraph>
                            ))}
                        </>
                    }
                    onOpenChange={(value) => setPopoverOpen(value)}
                    open={popoverOpen}
                    title={<Locale stringKey="COMPANY_FORM__GALLERY__ERROR__HEADER" />}
                >
                    {/* https://github.com/ant-design/ant-design/issues/15909#issuecomment-1030778255 */}
                    <div className={styles.input_image_list_item__image}>{media}</div>
                </Popover>
            </div>
        );
    }

    if (modifications && modifications.length > 0) {
        return (
            <div className={fullClassName}>
                <Popover
                    content={
                        <ul className={styles.modifications_list}>
                            {modifications.map((message) => (
                                <li key={message}>
                                    <Typography.Text className={styles.error_text}>{message}</Typography.Text>
                                </li>
                            ))}
                        </ul>
                    }
                    onOpenChange={(value) => setModificationsPopoverOpen(value)}
                    open={modificationsPopoverOpen}
                    title={<Locale stringKey="COMPANY_FORM__GALLERY__MODIFICATIONS__HEADER" />}
                    zIndex={2}
                >
                    {/* https://github.com/ant-design/ant-design/issues/15909#issuecomment-1030778255 */}
                    <div className={styles.input_image_list_item__image}>
                        <FontAwesomeIcon
                            className={styles.input_image_list_item__modifications}
                            color="white"
                            icon={faArrowsRotate}
                        />
                        {media}
                    </div>
                </Popover>
            </div>
        );
    }

    return <div className={fullClassName}>{media}</div>;
}
